import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, PencilIcon, TrashIcon, Store } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle } from 'lucide-react';
import { storeManagementService, IShopifyStore } from '../../services/StoreManagementService';
import StoreManagementDialog from '../dialogs/StoreManagementDialog';
import { useNavigate } from 'react-router-dom';

interface StoreListProps {
  onStoreChange?: () => void;
}

const StoreList: React.FC<StoreListProps> = ({ onStoreChange }) => {
  const [stores, setStores] = useState<IShopifyStore[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStore, setSelectedStore] = useState<IShopifyStore | undefined>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();

  const loadStores = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const storeList = await storeManagementService.getStores();
      setStores(storeList);
    } catch (err: any) {
      console.error('Failed to load stores:', err);
      if (err.message.includes('Authentication expired')) {
        // Redirect to login if authentication has expired
        navigate('/');
        return;
      }
      setError(err.message || 'Failed to load stores');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, setIsLoading, setError, setStores]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const handleAddStore = () => {
    setSelectedStore(undefined);
    setIsDialogOpen(true);
  };

  const handleEditStore = (store: IShopifyStore) => {
    setSelectedStore(store);
    setIsDialogOpen(true);
  };

  const handleDeleteStore = async (storeId: string) => {
    if (!window.confirm('Are you sure you want to delete this store?')) {
      return;
    }

    try {
      setIsLoading(true);
      await storeManagementService.deleteStore(storeId);
      await loadStores();
      onStoreChange?.();
    } catch (err: any) {
      setError(err.message || 'Failed to delete store');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogSuccess = () => {
    loadStores();
    onStoreChange?.();
  };

  if (isLoading && stores.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Connected Stores</h2>
        <Button onClick={handleAddStore} className="bg-blue-600 hover:bg-blue-700">
          Add Store
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {stores.length === 0 && !isLoading ? (
        <div className="text-center py-8">
          <Store className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No stores connected</h3>
          <p className="text-gray-500">
            Add your first Shopify store to get started.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {stores.map((store) => (
            <Card key={store.id} className="relative">
              {isLoading && (
                <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              )}
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-2">
                    <Store className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-lg">{store.storeName}</CardTitle>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditStore(store)}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => store.id && handleDeleteStore(store.id)}
                    >
                      <TrashIcon className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
                <CardDescription>{store.storeUrl}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-500">
                  Last updated: {store.updatedAt.toLocaleDateString()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <StoreManagementDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        store={selectedStore}
        onSuccess={handleDialogSuccess}
      />
    </div>
  );
};

export default StoreList;
